// material-ui
import { styled, useTheme } from '@mui/material/styles';
import {
    Avatar,
    Card,
    CardContent,
    Chip,
    Grid,
    LinearProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography
} from '@mui/material';
import { linearProgressClasses } from '@mui/material/LinearProgress';

// assets
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { FormattedMessage } from 'react-intl';
import useAuth from 'hooks/useAuth';
import { convertBtoGB } from 'utils/pay';

// styles
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 30,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.light : '#fff'
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main
    }
}));

const CardStyle = styled(Card)(({ theme }) => ({
    background: '#F6F8FA',
    marginBottom: '22px',
    overflow: 'hidden',
    position: 'relative',
    '&:after': {
        content: '""',
        position: 'absolute',
        width: '157px',
        height: '157px',
        background: '#CFF5F6',
        borderRadius: '50%',
        top: '-105px',
        right: '-96px'
    }
}));

const DateCardStyle = styled(Card)(({ theme }) => ({
    background: '#F6F8FA',
    marginBottom: '22px',
    overflow: 'hidden',
    position: 'relative'
}));

interface LinearProgressWithLabelProps {
    value: number;
}

// ==============================|| PROGRESS BAR WITH LABEL ||============================== //

function LinearProgressWithLabel({ value, ...others }: LinearProgressWithLabelProps) {
    const theme = useTheme();

    return (
        <Grid container direction="column" spacing={1} sx={{ mt: 1.5 }}>
            <Grid item>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Typography
                            variant="h6"
                            sx={{ color: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary[800] }}
                        >
                            <FormattedMessage id="traffic-utilization-rate" />
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="h6" color="inherit">{`${Math.round(value)}%`}</Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <BorderLinearProgress aria-label="progress of theme" variant="determinate" value={value} {...others} />
            </Grid>
        </Grid>
    );
}

// ==============================|| SIDEBAR - MENU CARD ||============================== //

const MenuCard = () => {
    const theme = useTheme();
    const { user } = useAuth();

    return (
        <>
            <CardStyle>
                <CardContent sx={{ p: 2 }}>
                    <List sx={{ p: 0, m: 0 }}>
                        <ListItem alignItems="flex-start" disableGutters sx={{ p: 0 }}>
                            <ListItemAvatar sx={{ mt: 0 }}>
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        ...theme.typography.commonAvatar,
                                        ...theme.typography.largeAvatar,
                                        color: theme.palette.primary.main,
                                        border: theme.palette.mode === 'dark' ? '1px solid' : 'none',
                                        borderColor: theme.palette.primary.main,
                                        background: theme.palette.mode === 'dark' ? theme.palette.dark.dark : '#fff',
                                        marginRight: '12px'
                                    }}
                                >
                                    <TableChartOutlinedIcon fontSize="inherit" />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                sx={{ mt: 0 }}
                                primary={
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary[800]
                                        }}
                                    >
                                        <FormattedMessage id="traffic-usage" />
                                    </Typography>
                                }
                                secondary={
                                    <Typography variant="caption">
                                        {convertBtoGB(user.stat.total).toFixed(2)}/{convertBtoGB(user.stat.quota).toFixed(0)} GB
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                    <LinearProgressWithLabel value={(user.stat.total / user.stat.quota) * 100} />
                </CardContent>
            </CardStyle>
            <DateCardStyle>
                <CardContent
                    sx={{
                        pl: 2,
                        pr: 2,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        display: 'flex',
                        '&.MuiCardContent-root': {
                            padding: '6px 14px'
                        }
                    }}
                >
                    <Typography variant="caption">
                        <FormattedMessage id="expires-on" />
                    </Typography>
                    <Chip label={user.expired_date} size="small" sx={{ fontWeight: 900, color: '#FFF', bgcolor: '#F3B735' }} />
                </CardContent>
            </DateCardStyle>
        </>
    );
};

export default MenuCard;
